import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { EmptyState, LoadingBox } from "@/components/ui";
import { HoverPopover } from "../components/hover-popover";
import { MarkdownBlock } from "../components/markdown-block";
import { TestReviewSwiper } from "../components/test-review-swiper";
import { useApiQuery } from "../hooks/use-api-query";
import type { DiscoveryRun } from "../types";
import { markdownToPlainText } from "../utils/text";

export default function DiscoveryReviewPage() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  // Fetch discovery run details for the header
  const { data: discoveryRun, isLoading } = useApiQuery<DiscoveryRun>(`/discovery-runs/${runId}`, {
    enabled: !!runId,
  });

  const handleBackClick = () => {
    navigate(`/discovery-runs/${runId}?tab=review`);
  };

  if (isLoading) {
    return <LoadingBox sx={{ height: "100%", minHeight: "400px" }} />;
  }

  if (!runId) {
    return <EmptyState message="No discovery run ID provided." />;
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          py: 1.5,
          px: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          size="small"
          color="primary"
          variant="outlined"
          fullWidth
          sx={{ minWidth: 180 }}
        >
          Back to Discovery
        </Button>
        {discoveryRun?.intent && (
          <HoverPopover
            content={<MarkdownBlock content={discoveryRun.intent} />}
            maxWidth={800}
            maxHeight={400}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
            >
              {markdownToPlainText(discoveryRun.intent)}
            </Typography>
          </HoverPopover>
        )}
      </Stack>

      {/* Review Swiper */}
      <Box
        sx={{
          p: 2,
          height: "calc(100vh - 160px)",
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
          }}
        >
          <TestReviewSwiper discoveryRunId={runId} maxWidth="800px" />
        </Box>
      </Box>
    </Box>
  );
}
